import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, SegmentedControl, SparkIcon, TooltipTrigger, sparkToast } from '@spark';

import { useAuth } from '@/features/auth/AuthContext';
import '../settings.css';

// Alias is derived from auth mode — not user-selectable.
// same_project → dev, cross_project → prod (max one target per alias).
function aliasForAuthMode(authMode) {
    return authMode === 'cross_project' ? 'prod' : 'dev';
}

// Artifact Registry or legacy GCR — mirrors backend deployment_config_builder.
const GCR_AR_URI_RE =
    /^[a-z0-9-]+-docker\.pkg\.dev\/[a-z][a-z0-9-]*\/[a-z0-9][a-z0-9._-]*\/[a-z0-9][a-z0-9._/-]+$/;
const GCR_LEGACY_URI_RE =
    /^(?:[a-z0-9-]+\.)?gcr\.io\/[a-z0-9][a-z0-9-]*\/[a-z0-9][a-z0-9._/-]+$/;

function validateGcrRepositoryUri(uri) {
    const trimmed = (uri || '').trim();
    if (!trimmed) return 'GCR repository URI is required';

    const arMatch = trimmed.match(/^([a-z0-9-]+-docker\.pkg\.dev)\/(.+)$/);
    if (arMatch) {
        const [, host, path] = arMatch;
        const segments = path.split('/').filter(Boolean);
        if (segments.length < 3) {
            const project = segments[0] || 'my-project';
            const repo = segments[1] || 'my-repo';
            return (
                `Add an image name after the repository. Example: ${host}/${project}/${repo}/my-image ` +
                `(not just .../${repo})`
            );
        }
        if (!GCR_AR_URI_RE.test(trimmed)) {
            return 'Use only lowercase letters, numbers, hyphens, underscores, and dots in the path';
        }
        return null;
    }

    const legacyMatch = trimmed.match(/^((?:[a-z0-9-]+\.)?gcr\.io)\/(.+)$/);
    if (legacyMatch) {
        const [, host, path] = legacyMatch;
        const segments = path.split('/').filter(Boolean);
        if (segments.length < 2) {
            const project = segments[0] || 'my-project';
            return (
                `Add an image name after the project. Example: ${host}/${project}/my-image ` +
                `(not just .../${project})`
            );
        }
        if (!GCR_LEGACY_URI_RE.test(trimmed)) {
            return 'Use only lowercase letters, numbers, hyphens, underscores, and dots in the path';
        }
        return null;
    }

    return (
        'Use <location>-docker.pkg.dev/<project>/<repo>/<image> ' +
        'or gcr.io/<project>/<image>'
    );
}

function normalizeTargets(targets) {
    return (targets || []).map((t) => {
        const auth_mode = t.auth_mode || 'same_project';
        return { uri: t.uri || '', auth_mode, alias: aliasForAuthMode(auth_mode) };
    });
}

function targetsEqual(a, b) {
    if (a.length !== b.length) return false;
    return a.every((t, i) => {
        const o = b[i];
        return (
            (t.uri || '') === (o.uri || '') &&
            (t.auth_mode || '') === (o.auth_mode || '') &&
            (t.alias || '') === (o.alias || '')
        );
    });
}

const AUTH_MODE_ITEMS = [
    { id: 'same_project', label: 'Same project' },
    { id: 'cross_project', label: 'Cross project' },
];

/**
 * GCR / Artifact Registry push targets (deployment) settings — Spark rebuild.
 *
 * Labelled **GCP - GCR** in the product; destinations are modern Artifact
 * Registry URIs (or legacy gcr.io). Auth:
 * - same-project: Workload Identity / ADC — no credentials needed.
 * - cross-project: customer grants Token Creator on their push SA to the
 *   platform GSA; the push SA email is configured once at org level.
 */
const GcrCredentialsForm = ({ settings, onGcrSettingsUpdate }) => {
    const { role, isAuthEnabled } = useAuth();
    const isAdmin = !isAuthEnabled || role === 'Admin';

    const [targets, setTargets] = useState(normalizeTargets(settings?.gcp_gcr_targets));
    const [crossProjectSaEmail, setCrossProjectSaEmail] = useState(
        settings?.gcp_gcr_cross_project_sa_email || ''
    );
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setTargets(normalizeTargets(settings?.gcp_gcr_targets));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(settings?.gcp_gcr_targets || [])]);

    useEffect(() => {
        setCrossProjectSaEmail(settings?.gcp_gcr_cross_project_sa_email || '');
    }, [settings?.gcp_gcr_cross_project_sa_email]);

    const savedTargets = useMemo(
        () => normalizeTargets(settings?.gcp_gcr_targets),
        [settings?.gcp_gcr_targets]
    );

    const targetsDirty = !targetsEqual(targets, savedTargets);
    const saEmailDirty = crossProjectSaEmail !== (settings?.gcp_gcr_cross_project_sa_email || '');
    const isDirty = targetsDirty || saEmailDirty;

    const targetUriErrors = targets.map((t) => validateGcrRepositoryUri(t.uri));
    const hasTargetErrors = targetUriErrors.some(Boolean);

    const crossProjectMissingSa =
        targets.some((t) => t.auth_mode === 'cross_project') &&
        !(crossProjectSaEmail || '').trim();

    const hasSameProject = targets.some((t) => t.auth_mode === 'same_project');
    const hasCrossProject = targets.some((t) => t.auth_mode === 'cross_project');

    const handleTargetChange = (index, key, value) => {
        setTargets((prev) =>
            prev.map((t, i) => {
                if (i !== index) return t;
                const updated = { ...t, [key]: value };
                if (key === 'auth_mode') updated.alias = aliasForAuthMode(value);
                return updated;
            })
        );
    };

    const handleAddTarget = (authMode) => {
        setTargets((prev) => [
            ...prev,
            { uri: '', auth_mode: authMode, alias: aliasForAuthMode(authMode) },
        ]);
    };

    const handleRemoveTarget = (index) => {
        setTargets((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = () => {
        if (hasTargetErrors) {
            const firstError = targetUriErrors.find(Boolean);
            if (firstError) {
                sparkToast.error('Invalid GCR repository URI', { content: firstError });
            }
            return;
        }
        const fields = {
            gcp_gcr_targets: targets.map((t) => {
                const auth_mode = t.auth_mode || 'same_project';
                return { uri: (t.uri || '').trim(), auth_mode, alias: aliasForAuthMode(auth_mode) };
            }),
            gcp_gcr_cross_project_sa_email: crossProjectSaEmail.trim() || null,
        };
        setIsSaving(true);
        onGcrSettingsUpdate(fields, 'saved');
        setTimeout(() => setIsSaving(false), 500);
    };

    return (
        <div className="settings-providers__panel">
            <p className="settings-text settings-providers__description fs-unmask">
                Add Artifact Registry repositories. Each target is automatically
                aliased: same-project → dev, cross-project → prod. Same-project
                targets use the platform pod&apos;s Workload Identity / ADC;
                cross-project targets impersonate the push SA configured below.
            </p>

            <h3 className="settings-subheading fs-unmask">Targets</h3>

            {targets.length === 0 ? (
                <p className="settings-text fs-unmask">
                    No GCR targets configured yet. Add one to enable
                    &quot;Push to GCR&quot;.
                </p>
            ) : null}

            {targets.map((target, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={index} className="settings-target-row">
                    <div className="settings-target-row__field">
                        <Input
                            size="large"
                            showLabel={false}
                            value={target.uri}
                            onChange={(e) => handleTargetChange(index, 'uri', e.target.value)}
                            placeholder="us-east1-docker.pkg.dev/specificai-479409/specificai-inference/my-model"
                            disabled={!isAdmin}
                            className="settings-secret__input"
                        />
                        {targetUriErrors[index] ? (
                            <p className="settings-field-error">{targetUriErrors[index]}</p>
                        ) : null}
                    </div>
                    <TooltipTrigger
                        text={
                            target.auth_mode === 'same_project'
                                ? 'Uses Workload Identity / ADC - no credentials needed'
                                : 'Impersonates the push SA configured below'
                        }
                    >
                        <span>
                            <SegmentedControl
                                size="small"
                                items={AUTH_MODE_ITEMS.map((item) => ({
                                    ...item,
                                    disabled: !isAdmin,
                                }))}
                                activeId={target.auth_mode}
                                onChange={(value) => handleTargetChange(index, 'auth_mode', value)}
                            />
                        </span>
                    </TooltipTrigger>
                    <span className="settings-target-row__alias">{target.alias}</span>
                    <Button
                        size="tiny"
                        variant="ghost"
                        iconSlug="trash"
                        aria-label="Remove target"
                        startIcon={<SparkIcon slug="trash" size="small" />}
                        onClick={() => handleRemoveTarget(index)}
                        disabled={!isAdmin}
                    />
                </div>
            ))}

            <div className="settings-form__row">
                {!hasSameProject ? (
                    <Button
                        size="tiny"
                        variant="ghost"
                        showStartIcon
                        onClick={() => handleAddTarget('same_project')}
                        disabled={!isAdmin}
                    >
                        Add Dev Target
                    </Button>
                ) : null}
                {!hasCrossProject ? (
                    <Button
                        size="tiny"
                        variant="ghost"
                        showStartIcon
                        onClick={() => handleAddTarget('cross_project')}
                        disabled={!isAdmin}
                    >
                        Add Prod Target
                    </Button>
                ) : null}
            </div>

            <h3 className="settings-subheading fs-unmask">Cross-project push SA</h3>
            <p className="settings-text settings-providers__description fs-unmask">
                Only used for cross-project targets: in the customer project,
                grant roles/iam.serviceAccountTokenCreator on the push SA below
                to the platform Workload Identity SA.
            </p>
            {crossProjectMissingSa ? (
                <p className="settings-field-error fs-unmask">
                    A cross-project target is configured but no push SA email is
                    set - pushes will fail until this is filled in.
                </p>
            ) : null}
            <Input
                size="large"
                label="Push SA email"
                value={crossProjectSaEmail}
                onChange={(e) => setCrossProjectSaEmail(e.target.value)}
                placeholder="push-sa@customer-project.iam.gserviceaccount.com"
                disabled={!isAdmin}
                className="settings-secret__input"
            />

            <div className="settings-form__footer">
                <Button
                    size="tiny"
                    variant="primary"
                    onClick={handleSave}
                    disabled={!isAdmin || !isDirty || hasTargetErrors || isSaving}
                    disableWhenReadOnly
                >
                    Save Changes
                </Button>
            </div>
        </div>
    );
};

export default GcrCredentialsForm;
